import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, VideoOff, Video, CalendarDays } from "lucide-react";
import JitsiCall from "@/components/WebRTCCall";
import CallFiles from "@/components/CallFiles";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LOGO = "https://content.webfactorysite.co.uk/20260113092111-Wales-Event-Logo.png?t=1768296071";

export default function PublicCall() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [inCall, setInCall] = useState(false);
  const [nameErr, setNameErr] = useState("");

  useEffect(() => {
    document.title = "Video Call — Wales Event Medical Services";
    fetch(`${BACKEND}/api/public/call/${token}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("bad"))))
      .then(setInfo)
      .catch(() => setErr("This call link isn't valid — please contact Wales Event Medical Services."));
  }, [token]);

  const join = () => {
    if (!name.trim()) { setNameErr("Type your name so we know who's joining"); return; }
    setNameErr("");
    setInCall(true);
  };

  if (err) {
    return (
      <div className="min-h-screen bg-[#0F2547] grid place-items-center p-6 text-center" data-testid="call-error-page">
        <div>
          <VideoOff className="h-10 w-10 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-300 text-sm">{err}</p>
        </div>
      </div>
    );
  }
  if (!info) return <div className="min-h-screen bg-[#0F2547] grid place-items-center"><Loader2 className="h-8 w-8 text-[#00D2C2] animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#0B0F14] text-slate-200" data-testid="public-call">
      <header className="border-b border-white/10 px-4 sm:px-8 py-5 flex items-center gap-3">
        <img src={LOGO} alt="Wales Event Medical Services" className="h-11 w-auto bg-white rounded-lg px-2 py-1" />
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#00D2C2]">Video call</p>
          <h1 className="text-white font-bold text-base sm:text-lg truncate" data-testid="call-title">{info.title}</h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 sm:p-8">
        {!inCall ? (
          <div className="max-w-md mx-auto rounded-2xl border border-slate-700 bg-[#0F141B] p-6 space-y-4" data-testid="call-prejoin">
            <p className="text-sm text-slate-300 flex items-center gap-2">
              <CalendarDays size={16} className="text-[#00D2C2]" />
              <strong className="text-white">{[info.date, info.time].filter(Boolean).join(" · ")}</strong>
              {info.duration ? <span className="text-slate-400">· approx. {info.duration} min</span> : null}
            </p>
            <p className="text-xs text-slate-400">Join from your phone or laptop — no app needed. Allow camera & microphone when your browser asks. You may wait in a lobby until we let you in.</p>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
              className="w-full h-12 rounded-lg border border-slate-600 bg-[#121820] px-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#00D2C2]"
              data-testid="call-name-input" />
            {nameErr && <p className="text-xs text-red-400" data-testid="call-name-error">{nameErr}</p>}
            <button onClick={join} data-testid="call-join-btn"
              className="w-full h-12 rounded-full bg-[#00D2C2] hover:opacity-90 text-[#06201D] text-sm font-extrabold inline-flex items-center justify-center gap-2 transition-opacity">
              <Video size={16} /> Join the call
            </button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_280px] gap-4" data-testid="call-live">
            <JitsiCall token={token} name={name.trim()} onEnded={() => setInCall(false)} />
            <CallFiles token={token} by={name.trim()} dark testid="call-files" />
          </div>
        )}
        <p className="text-center text-[11px] text-slate-500 pt-6">Wales Event Medical Services · info@waleseventmedical.co.uk</p>
      </main>
    </div>
  );
}
